import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { HelpText } from '#components/atoms/forms/index.ts';
import { MeatballMenu, MenuItem } from '#components/patterns/menus/meatball-menu/index.ts';
import { Table } from '#components/patterns/table/Table.tsx';
import type { UnknownConfigOverride } from '#util/unknownConfigOverrides.ts';

/** An unknown override plus its index in the source list, so Delete/Restore can address it. */
type Row = UnknownConfigOverride & { index: number; removed: boolean };

type Props = {
  /** Every saved unknown override, including ones pending delete */
  items: UnknownConfigOverride[];
  /** Section header rendered above the list. Omit when the surrounding markup already titles it. */
  heading?: string;
  /** Indices into `items` that are pending delete; rendered struck through with a Restore action */
  removedIndices?: ReadonlySet<number>;
  /** Omit to hide the action menu and render purely read-only (e.g. managed hosts). */
  onRemove?: (index: number) => void;
  onRestore?: (index: number) => void;
  className?: string;
};

const columnHelper = createColumnHelper<Row>();

/** Unknown values have no schema to format against; show strings bare and everything else as JSON. */
export function formatUnknownConfigOverrideValue(value: unknown): string {
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

/** Deletes only apply on save, so a pending delete stays visible and can still be restored. */
function RemovableCell({ removed, children }: { removed: boolean; children: React.ReactNode }) {
  if (!removed) return <>{children}</>;
  return <s>{children}</s>;
}

/**
 * Read-only list of config overrides the webclient has no editor for. They stay on the resource
 * when it's saved (see `withUnknownOverrides`), so the user can at least see and delete them.
 */
export function UnknownConfigOverridesList({ items, heading, removedIndices, onRemove, onRestore, className }: Props) {
  const rows = useMemo(
    () => items.map((item, index) => ({ ...item, index, removed: Boolean(removedIndices?.has(index)) })),
    [items, removedIndices]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('key', {
        header: 'Option',
        cell: (info) => (
          <RemovableCell removed={info.row.original.removed}>
            <code>{info.getValue()}</code>
            {info.row.original.removed ?
              <span className="visually-hidden"> (deleted when you save)</span>
            : null}
          </RemovableCell>
        ),
      }),
      columnHelper.accessor('value', {
        header: 'Value',
        size: 200,
        cell: (info) => (
          <RemovableCell removed={info.row.original.removed}>
            <code>{formatUnknownConfigOverrideValue(info.getValue())}</code>
          </RemovableCell>
        ),
      }),
      ...(onRemove ?
        [
          columnHelper.display({
            id: 'unknown-config-override-action-menu',
            header: '',
            size: 34,
            cell: (info) => (
              <div className="flex justify-end">
                <MeatballMenu>
                  {info.row.original.removed && onRestore ?
                    <MenuItem
                      onClick={() => {
                        onRestore(info.row.original.index);
                      }}
                    >
                      Restore
                    </MenuItem>
                  : <MenuItem
                      onClick={() => {
                        onRemove(info.row.original.index);
                      }}
                    >
                      Delete
                    </MenuItem>
                  }
                </MeatballMenu>
              </div>
            ),
          }),
        ]
      : []),
    ],
    [onRemove, onRestore]
  );

  if (!items.length) {
    return null;
  }

  return (
    <div className={className}>
      {heading ?
        <h3 className="h5 mb-4">{heading}</h3>
      : null}
      <HelpText className="mb-8">
        Some options cannot be set or modified via the admin panel. These options may be deprecated or experimental. You
        can delete them or save them as-is.
      </HelpText>
      {/* No caption: both render sites already title the section (the heading here, or a fieldset legend) */}
      <Table data={rows} columns={columns} />
    </div>
  );
}
